import './ActionActivityCenter.css';

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { ConfigProvider, Progress, Tooltip, message } from 'antd';
import {
  ChevronDown16Regular,
  ChevronUp16Regular,
  Dismiss16Regular,
  Open16Regular,
} from '@fluentui/react-icons';

import { DMS_TOOLTIP_Z_INDEX, dmsAntdTheme } from '../../config/antdTheme';
import {
  acknowledgeJustLaunchedAction,
  clearActionTrackingState,
  dismissActionTracker,
  selectActiveTrackers,
  selectJustLaunchedActionId,
  setActionTrackerExpanded,
} from '../../redux/slices/actionsSlice';
import { AppDispatch } from '../../redux/store/store';
import ActionStatusIcon from '../common/ActionStatusIcon';
import ActionTargetRow from '../common/ActionTargetRow';
import ProgressDialog from '../RTUActions/ProgressDialog/ProgressDialog';
import { resolveActionDisplayStatus } from '../../utils/actionStatusConfig';
import {
  formatActionProgressSummary,
  formatActionStatusCounts,
  summarizeActionProgress,
} from '../../utils/actionProgress';
import { actionRunTitle } from '../../utils/actionPresentation';
import { ActionArtifact } from '../../types/actionTypes';
import actionRunsService from '../../services/actionRunsService';
import { sanitizeApiError } from '../../utils/apiErrorSanitizer';

interface ActionActivityCenterProps {
  maxVisible?: number;
}

/**
 * One consolidated activity panel for every in-flight or recently finished
 * action (ACT-UI-F correction wave). Each parent row can expand to reveal
 * compact target rows; clicking the parent title opens the full progress
 * modal, while clicking an expanded target navigates to that RTU's Actions
 * page with the parent action selected. Actions stay listed until the user
 * dismisses them individually, or the whole panel is closed (which only
 * clears this tracker presentation, never backend state).
 */
const ActionActivityCenter: React.FC<ActionActivityCenterProps> = ({ maxVisible = 6 }) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const trackers = useSelector(selectActiveTrackers);
  const justLaunchedActionId = useSelector(selectJustLaunchedActionId);
  const [collapsed, setCollapsed] = useState(false);
  const [openActionId, setOpenActionId] = useState<number | null>(null);
  const [highlightedActionId, setHighlightedActionId] = useState<number | null>(null);
  const [closedByUser, setClosedByUser] = useState(false);
  const [downloadingArtifactId, setDownloadingArtifactId] = useState<number | null>(null);

  useEffect(() => {
    const clearOnLogout = () => {
      dispatch(clearActionTrackingState());
      setOpenActionId(null);
      setHighlightedActionId(null);
      setClosedByUser(false);
    };
    window.addEventListener('dms-auth-logout', clearOnLogout);
    return () => window.removeEventListener('dms-auth-logout', clearOnLogout);
  }, [dispatch]);

  // A newly launched action reopens the panel, selects and expands its row.
  // The progress modal itself is opened by the launching surface (e.g. the
  // RTU action launcher); this only makes the panel reflect that action so
  // it is already in the right state once that dialog is closed - it must
  // never open a second modal for the same action.
  useEffect(() => {
    if (justLaunchedActionId === null) return;
    setClosedByUser(false);
    setCollapsed(false);
    setHighlightedActionId(justLaunchedActionId);
    dispatch(setActionTrackerExpanded({ actionId: justLaunchedActionId, expanded: true }));
    dispatch(acknowledgeJustLaunchedAction());
  }, [dispatch, justLaunchedActionId]);

  const openEntry = openActionId
    ? trackers.find(({ action }) => action.id === openActionId)
    : undefined;

  if (trackers.length === 0 || closedByUser) {
    return null;
  }

  const visible = trackers.slice(0, maxVisible);
  const overflowCount = trackers.length - visible.length;

  const navigateToTarget = (actionId: number, rtuId: number) => {
    navigate(`/rtupage/${rtuId}?view=actions&actionRunId=${actionId}`);
  };

  const handleDownloadArtifact = async (actionId: number, artifact: ActionArtifact) => {
    setDownloadingArtifactId(artifact.id);
    try {
      await actionRunsService.downloadArtifact(actionId, artifact);
    } catch (error) {
      void message.error(sanitizeApiError(error, { endpointCategory: 'artifact-download', t }).message);
    } finally {
      setDownloadingArtifactId(null);
    }
  };

  return (
    <ConfigProvider direction={i18n.dir()} theme={dmsAntdTheme}>
      <div className="action-activity-center" role="region" aria-label={t('tracker.activityCenter.title', { defaultValue: 'Action activity' })}>
        <div className="action-activity-center__panel">
          <div className="action-activity-center__header">
            <button
              type="button"
              className="action-activity-center__header-toggle"
              onClick={() => setCollapsed((value) => !value)}
              aria-expanded={!collapsed}
              aria-label={collapsed
                ? t('tracker.activityCenter.expand', { defaultValue: 'Expand action activity' })
                : t('tracker.activityCenter.collapse', { defaultValue: 'Collapse action activity' })}
            >
              <span className="action-activity-center__title">
                {t('tracker.activityCenter.title', { defaultValue: 'Action activity' })}
              </span>
              <span className="action-activity-center__count">{trackers.length}</span>
              {collapsed
                ? <ChevronUp16Regular aria-hidden="true" />
                : <ChevronDown16Regular aria-hidden="true" />}
            </button>
            <Tooltip title={t('tracker.activityCenter.openAll', { defaultValue: 'Open All Actions' })} zIndex={DMS_TOOLTIP_Z_INDEX}>
              <button
                type="button"
                className="action-activity-center__open-all"
                aria-label={t('tracker.activityCenter.openAll', { defaultValue: 'Open All Actions' })}
                onClick={() => navigate('/actions')}
              >
                <Open16Regular aria-hidden="true" />
              </button>
            </Tooltip>
            <Tooltip title={t('tracker.activityCenter.close', { defaultValue: 'Close activity panel' })} zIndex={DMS_TOOLTIP_Z_INDEX}>
              <button
                type="button"
                className="action-activity-center__close"
                aria-label={t('tracker.activityCenter.close', { defaultValue: 'Close activity panel' })}
                onClick={() => setClosedByUser(true)}
              >
                <Dismiss16Regular aria-hidden="true" />
              </button>
            </Tooltip>
          </div>

          {!collapsed ? (
            <ul className="action-activity-center__list">
              {visible.map(({ tracker, action }) => {
                const status = resolveActionDisplayStatus(action);
                const summary = summarizeActionProgress(action);
                const counts = formatActionStatusCounts(summary, t);
                const title = actionRunTitle(action as any, t);
                const expanded = tracker.expanded;
                return (
                  <li
                    key={action.id}
                    className={`action-activity-center__row${highlightedActionId === action.id ? ' action-activity-center__row--selected' : ''}`}
                  >
                    <div className="action-activity-center__row-main">
                      <button
                        type="button"
                        className="action-activity-center__expand-toggle"
                        aria-expanded={expanded}
                        aria-label={expanded
                          ? t('tracker.activityCenter.collapseAction', { defaultValue: 'Hide targets' })
                          : t('tracker.activityCenter.expandAction', { defaultValue: 'Show targets' })}
                        onClick={() => dispatch(setActionTrackerExpanded({ actionId: action.id, expanded: !expanded }))}
                      >
                        {expanded
                          ? <ChevronUp16Regular aria-hidden="true" />
                          : <ChevronDown16Regular aria-hidden="true" />}
                      </button>
                      <ActionStatusIcon status={status} />
                      <button
                        type="button"
                        className="action-activity-center__row-title-button"
                        onClick={() => { setOpenActionId(action.id); setHighlightedActionId(action.id); }}
                      >
                        <span className="action-activity-center__row-text">
                          <span className="action-activity-center__row-title">{title}</span>
                          <span className="action-activity-center__row-meta">
                            {formatActionProgressSummary(summary, t)}
                            {counts ? ` · ${counts}` : ''}
                          </span>
                          <Progress
                            percent={Math.round(summary.progressRatio * 100)}
                            showInfo={false}
                            size="small"
                            status={summary.progressRatio >= 1 ? 'normal' : 'active'}
                            className="action-activity-center__row-progress"
                          />
                        </span>
                      </button>
                      <Tooltip title={t('tracker.dismiss')} zIndex={DMS_TOOLTIP_Z_INDEX}>
                        <button
                          type="button"
                          className="action-activity-center__dismiss"
                          aria-label={t('tracker.dismiss')}
                          onClick={() => {
                            dispatch(dismissActionTracker(action.id));
                            if (openActionId === action.id) setOpenActionId(null);
                            if (highlightedActionId === action.id) setHighlightedActionId(null);
                          }}
                        >
                          <Dismiss16Regular aria-hidden="true" />
                        </button>
                      </Tooltip>
                    </div>
                    {expanded ? (
                      <ul className="action-activity-center__targets">
                        {action.actionDetails.map((detail, index) => {
                          const rtuId = detail.rtuId ?? null;
                          const name = detail.rtuName
                            ? (rtuId !== undefined && rtuId !== null ? `${detail.rtuName} (${rtuId})` : detail.rtuName)
                            : rtuId !== undefined && rtuId !== null
                              ? t('actionsWorkbench.rtuTarget', { defaultValue: 'RTU {{rtuId}}', rtuId })
                              : t('actionsWorkbench.unknownRtu', { defaultValue: 'Unknown RTU' });
                          const artifact = action.artifacts?.find((item) => item.targetRunId === detail.id);
                          return (
                            <li key={String(detail.id ?? `${rtuId ?? 'unknown'}:${index}`)}>
                              <ActionTargetRow
                                status={detail.status}
                                name={name}
                                nameTooltip={detail.rtuSerialNo
                                  ? t('actionsModal.progress.serialTooltip', { defaultValue: 'Serial: {{serialNo}}', serialNo: detail.rtuSerialNo })
                                  : undefined}
                                phase={detail.phaseLabel}
                                durationMs={detail.durationMs}
                                transfer={detail.transfer}
                                onActivate={rtuId ? () => navigateToTarget(action.id, rtuId) : undefined}
                                onDownloadArtifact={artifact ? () => void handleDownloadArtifact(action.id, artifact) : undefined}
                                downloadLabel={artifact ? t('actionsModal.artifacts.download', { defaultValue: 'Download {{fileName}}', fileName: artifact.fileName }) : undefined}
                                downloadingArtifact={artifact ? downloadingArtifactId === artifact.id : false}
                                artifactFileName={artifact?.fileName}
                                compact
                              />
                            </li>
                          );
                        })}
                      </ul>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          ) : null}

          {!collapsed && overflowCount > 0 ? (
            <button
              type="button"
              className="action-activity-center__overflow"
              onClick={() => navigate('/actions')}
            >
              {t('tracker.moreCount', { defaultValue: '+{{count}} more in All Actions', count: overflowCount })}
            </button>
          ) : null}
        </div>
      </div>

      {openEntry ? (
        <ProgressDialog
          actionName={actionRunTitle(openEntry.action as any, t)}
          actionTimestamp={openEntry.action.createdAt}
          visible
          onHide={() => setOpenActionId(null)}
          actionId={openEntry.action.id}
        />
      ) : null}
    </ConfigProvider>
  );
};

export default ActionActivityCenter;
